import React, { useEffect } from "react";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import {
  DoorType,
  DoubleDoorType,
  SlidingDoorType,
  WindowType,
  VoidType,
  WallType,
  isDoorType,
  isSlidingDoorType,
  isDoubleDoorType,
  isWindowType,
  isVoidType,
  SymbolType,
} from "../../types/floorplannerTypes";
import { floorplannerStore } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { formatLength } from "../../utils/conversions";
import { calculateObjectPositionAndRotation } from "./calculateObjectPositionAndRotation";
import { SimpleLineSegments } from "./SimpleLineSegments";
import { SimpleText } from "../Text/SimpleText";
import { getSymbolInsertGeometry, getSymbolWidthInfo } from "../../utils/symbolAttachmentGeometry";
import { getWallArcLength } from "../../utils/arcUtils";

interface AttachmentMeasurementLinesProps {
  /** The attached symbol (Door, Window, DoubleDoor, or Void) */
  symbol: DoorType | SlidingDoorType | DoubleDoorType | WindowType | VoidType;
  /** The wall the symbol is attached to */
  attachedWall: WallType;
  /** Whether the symbol is currently being dragged */
  isDragging: boolean;
  /** Whether to always show measurements (future feature, defaults to false) */
  showAlways?: boolean;
}

/**
 * Calculates the start and end distances of a symbol along the wall axis.
 * 
 * For doors: position is at the hinge, so we need to account for this offset
 * For windows/voids: position is at the center
 * 
 * Returns { startDistance, endDistance } representing the symbol's edges along the wall.
 */
const calculateSymbolEdgeDistances = (
  symbol: SymbolType,
  symbolPositionDistance: number
): { startDistance: number; endDistance: number } => {
  if (isSlidingDoorType(symbol)) {
    // Sliding door: leaf + clear passage (2 × doorWidth) plus frames.
    const frameWidth = symbol.doorFrameWidth || floorplannerStore.doorFrameWidth;
    const startDistance = symbolPositionDistance - frameWidth;
    const endDistance = symbolPositionDistance + symbol.doorWidth * 2 + frameWidth;
    return { startDistance, endDistance };
  }

  if (isDoorType(symbol)) {
    // Door position is at the hinge
    // The door extends from: position - frameWidth to position + doorWidth + frameWidth
    const frameWidth = symbol.doorFrameWidth || floorplannerStore.doorFrameWidth;
    const startDistance = symbolPositionDistance - frameWidth;
    const endDistance = symbolPositionDistance + symbol.doorWidth + frameWidth;
    return { startDistance, endDistance };
  }
  
  if (isDoubleDoorType(symbol)) {
    // Double door position is at the left hinge
    // Total width = (doubleDoorWidth * 2) + (frameWidth * 2)
    // Extends from: position - frameWidth to position + (doubleDoorWidth * 2) + frameWidth
    const frameWidth = symbol.doorFrameWidth || floorplannerStore.doorFrameWidth;
    const startDistance = symbolPositionDistance - frameWidth;
    const endDistance = symbolPositionDistance + (symbol.doubleDoorWidth * 2) + frameWidth;
    return { startDistance, endDistance };
  }
  
  if (isWindowType(symbol)) {
    // Window position is at the center
    const halfWidth = symbol.windowLength / 2;
    return {
      startDistance: symbolPositionDistance - halfWidth,
      endDistance: symbolPositionDistance + halfWidth,
    };
  }
  
  if (isVoidType(symbol)) {
    // Void position is at the center
    const halfWidth = symbol.voidLength / 2;
    return {
      startDistance: symbolPositionDistance - halfWidth,
      endDistance: symbolPositionDistance + halfWidth,
    };
  }
  
  return { startDistance: symbolPositionDistance, endDistance: symbolPositionDistance };
};

/**
 * Calculates the distance of a symbol's position from the wall start point.
 * Uses the symbol's actual position rather than attachedDistance for real-time updates during drag.
 */
const calculateSymbolDistanceFromWallStart = (
  symbolPosition: THREE.Vector2,
  wallStart: THREE.Vector2,
  wallEnd: THREE.Vector2
): number => {
  const wallDirection = wallEnd.clone().sub(wallStart).normalize();
  const symbolOffset = symbolPosition.clone().sub(wallStart);
  return symbolOffset.dot(wallDirection);
};

/**
 * Renders measurement lines showing the distance from wall endpoints to an attached symbol.
 * Displays two measurements:
 * 1. Distance from wall start to symbol start edge
 * 2. Distance from symbol end edge to wall end
 *
 * These measurements help users precisely position doors, windows, and other
 * wall-attached symbols while dragging.
 */
const AttachmentMeasurementLinesComponent: React.FC<AttachmentMeasurementLinesProps> = observer(
  ({ symbol, attachedWall, isDragging, showAlways = false }) => {
    // Only show measurements while dragging (or always if showAlways is true)
    const shouldShow = isDragging || showAlways;
    const dragPreview = isDragging ? renderStore.getAttachmentDragPreview(symbol.id) : undefined;
    const measurementWall = dragPreview
      ? (dragPreview.wallId ? floorplannerStore.wallsMap.get(dragPreview.wallId) : undefined)
      : attachedWall;
    const measurementSymbol = dragPreview
      ? {
        ...symbol,
        position: dragPreview.position.clone(),
        rotation: dragPreview.rotation,
      } as DoorType | DoubleDoorType | WindowType | VoidType
      : symbol;

    // Track which wall has an attachment being dragged (for hiding wall measurement)
    useEffect(() => {
      if (isDragging && measurementWall) {
        editorStore.setAttachmentDraggingOnWall(measurementWall.id);
      } else {
        editorStore.setAttachmentDraggingOnWall(null);
      }
      return () => {
        // Cleanup when component unmounts
        if (isDragging) {
          editorStore.setAttachmentDraggingOnWall(null);
        }
      };
    }, [isDragging, measurementWall?.id]);

    // Check if measurements should be shown based on zoom level
    const shouldShowMeasurements = editorStore.shouldShowMeasurements();

    // Get measurement scale for consistent sizing
    const measurementScale = editorStore.getMeasurementVisibilityScale();

    // Styling constants
    const lineColor = "#888888"; // Grayed out color
    const textColor = "#666666";
    const stopLineLength = 0.1;

    // Calculate ruler offset from wall (same as MeasurementLine)
    if (!measurementWall) {
      return null;
    }

    const wallWidth = measurementWall.wallWidth || floorplannerStore.wallWidth;
    const rulerLineOffset = 0.2 + wallWidth / 2;

    // Calculate text box dimensions (scaled by zoom)
    const measurementZoomLevelDivisor = editorStore.measurementZoomLevelDivisor();
    const rulerTextBoxPadding = (0.13 / measurementZoomLevelDivisor) * measurementScale;
    const rulerTextBoxHeight = (0.16 / measurementZoomLevelDivisor) * measurementScale;
    const rulerTextBoxWidth = (0.3 / measurementZoomLevelDivisor) * measurementScale;
    const rulerTextFontSize = (14 / measurementZoomLevelDivisor) * measurementScale;

    const wallStart = new THREE.Vector2(measurementWall.start.x, measurementWall.start.y);
    const wallEnd = new THREE.Vector2(measurementWall.end.x, measurementWall.end.y);
    const wallDirectionVec = wallEnd.clone().sub(wallStart);
    const wallDirectionNormalized = wallDirectionVec.clone().normalize();
    const isArcWall = measurementWall.lineForm === "arc" && !!measurementWall.controlPoint;

    // For arc walls: use insert geometry (arc-length distances + chord-based world positions).
    // For straight walls: use the classic chord-projection approach.
    let symbolStartDistance: number;
    let symbolEndDistance: number;
    let symbolStartPoint: THREE.Vector2;
    let symbolEndPoint: THREE.Vector2;
    let wallLength: number;

    if (isArcWall) {
      // Find the attachment record for this symbol on this wall
      const attachment = measurementWall.symbolAttachments?.find(a => a.symbolId === measurementSymbol.id);
      const posFromStart = dragPreview?.positionFromStart ?? attachment?.positionFromStart ?? (() => {
        // Fallback: project symbol position onto chord
        const symbolPosition = new THREE.Vector2(measurementSymbol.position.x, measurementSymbol.position.y);
        return calculateSymbolDistanceFromWallStart(symbolPosition, wallStart, wallEnd);
      })();
      const insertGeom = getSymbolInsertGeometry(
        measurementWall, measurementSymbol, posFromStart, floorplannerStore.doorFrameWidth,
      );
      symbolStartDistance = insertGeom.sA;
      symbolEndDistance = insertGeom.sB;
      symbolStartPoint = insertGeom.pA.clone();
      symbolEndPoint = insertGeom.pB.clone();
      wallLength = getWallArcLength(measurementWall);
    } else {
      const symbolPosition = new THREE.Vector2(measurementSymbol.position.x, measurementSymbol.position.y);
      const symbolPositionDistance = calculateSymbolDistanceFromWallStart(symbolPosition, wallStart, wallEnd);
      const { startDistance, endDistance } = calculateSymbolEdgeDistances(measurementSymbol, symbolPositionDistance);
      symbolStartDistance = startDistance;
      symbolEndDistance = endDistance;
      symbolStartPoint = wallStart.clone().add(wallDirectionNormalized.clone().multiplyScalar(symbolStartDistance));
      symbolEndPoint = wallStart.clone().add(wallDirectionNormalized.clone().multiplyScalar(symbolEndDistance));
      wallLength = wallStart.distanceTo(wallEnd);
    }

    const distanceFromWallStart = Math.max(0, symbolStartDistance);
    const distanceFromWallEnd = Math.max(0, wallLength - symbolEndDistance);

    const { textRotation } = calculateObjectPositionAndRotation(measurementWall.start, measurementWall.end);

    const leftPerpendicular = new THREE.Vector2(-wallDirectionNormalized.y, wallDirectionNormalized.x);
    const rightPerpendicular = leftPerpendicular.clone().negate();

    let offsetPerp = leftPerpendicular.clone();
    if (wallDirectionVec.length() > 1e-6) {
      const leftExtent = floorplannerStore.getOutlineSideExtents(measurementWall, "left");
      const rightExtent = floorplannerStore.getOutlineSideExtents(measurementWall, "right");
      const chosenSide = floorplannerStore.getMeasurementDisplaySide(measurementWall, leftExtent, rightExtent);
      offsetPerp = chosenSide === "left" ? leftPerpendicular : rightPerpendicular;
    }

    const wallStartPoint = wallStart;
    const wallEndPoint = wallEnd;

    // Apply perpendicular offset for measurement line position
    const measurementWallStart = new THREE.Vector3(
      wallStartPoint.x + offsetPerp.x * rulerLineOffset,
      wallStartPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );
    const measurementSymbolStart = new THREE.Vector3(
      symbolStartPoint.x + offsetPerp.x * rulerLineOffset,
      symbolStartPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );
    const measurementSymbolEnd = new THREE.Vector3(
      symbolEndPoint.x + offsetPerp.x * rulerLineOffset,
      symbolEndPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );
    const measurementWallEnd = new THREE.Vector3(
      wallEndPoint.x + offsetPerp.x * rulerLineOffset,
      wallEndPoint.y + offsetPerp.y * rulerLineOffset,
      0
    );

    const stopLineOffset = offsetPerp.clone().multiplyScalar(stopLineLength);

    // Build segments for start measurement (wall start to symbol start)
    const startSegments: [number, number, number][] = [];
    if (distanceFromWallStart > 0.01) {
      // Main line
      startSegments.push([measurementWallStart.x, measurementWallStart.y, 0]);
      startSegments.push([measurementSymbolStart.x, measurementSymbolStart.y, 0]);

      // Start cap
      startSegments.push([
        measurementWallStart.x + stopLineOffset.x,
        measurementWallStart.y + stopLineOffset.y,
        0,
      ]);
      startSegments.push([
        measurementWallStart.x - stopLineOffset.x,
        measurementWallStart.y - stopLineOffset.y,
        0,
      ]);

      // End cap (at symbol start)
      startSegments.push([
        measurementSymbolStart.x + stopLineOffset.x,
        measurementSymbolStart.y + stopLineOffset.y,
        0,
      ]);
      startSegments.push([
        measurementSymbolStart.x - stopLineOffset.x,
        measurementSymbolStart.y - stopLineOffset.y,
        0,
      ]);
    }

    // Build segments for end measurement (symbol end to wall end)
    const endSegments: [number, number, number][] = [];
    if (distanceFromWallEnd > 0.01) {
      // Main line
      endSegments.push([measurementSymbolEnd.x, measurementSymbolEnd.y, 0]);
      endSegments.push([measurementWallEnd.x, measurementWallEnd.y, 0]);

      // Start cap (at symbol end)
      endSegments.push([
        measurementSymbolEnd.x + stopLineOffset.x,
        measurementSymbolEnd.y + stopLineOffset.y,
        0,
      ]);
      endSegments.push([
        measurementSymbolEnd.x - stopLineOffset.x,
        measurementSymbolEnd.y - stopLineOffset.y,
        0,
      ]);

      // End cap (at wall end)
      endSegments.push([
        measurementWallEnd.x + stopLineOffset.x,
        measurementWallEnd.y + stopLineOffset.y,
        0,
      ]);
      endSegments.push([
        measurementWallEnd.x - stopLineOffset.x,
        measurementWallEnd.y - stopLineOffset.y,
        0,
      ]);
    }

    // Calculate text positions (center of each measurement line)
    const startTextPosition = new THREE.Vector3(
      (measurementWallStart.x + measurementSymbolStart.x) / 2,
      (measurementWallStart.y + measurementSymbolStart.y) / 2,
      0.02
    );
    const endTextPosition = new THREE.Vector3(
      (measurementSymbolEnd.x + measurementWallEnd.x) / 2,
      (measurementSymbolEnd.y + measurementWallEnd.y) / 2,
      0.02
    );

    // Don't render if not showing or measurements are hidden by zoom
    if (!shouldShow || !shouldShowMeasurements) {
      return null;
    }

    return (
      <group position={[0, 0, 0.002]} userData={{ isAttachmentMeasurement: true }}>
        {/* Start measurement (wall start to symbol start) */}
        {distanceFromWallStart > 0.01 && startSegments.length > 0 && (
          <>
            <SimpleLineSegments
              segments={startSegments}
              color={lineColor}
              lineWidth={0.1}
              renderOrder={997}
              depthTest={false}
            />
            <group position={startTextPosition} rotation={[0, 0, textRotation]}>
              <mesh position={[0, 0, -0.01]} renderOrder={998}>
                <planeGeometry args={[rulerTextBoxWidth + rulerTextBoxPadding, rulerTextBoxHeight]} />
                <meshBasicMaterial
                  color={renderStore.labelBackgroundColor}
                  depthTest={false}
                  depthWrite={false}
                />
              </mesh>
              <SimpleText
                position={[0, 0, 0.01]}
                color={textColor}
                fontSize={rulerTextFontSize}
                renderOrder={999}
                depthTest={false}
              >
                {`${formatLength(distanceFromWallStart, editorStore.displayLengthUnit)}`}
              </SimpleText>
            </group>
          </>
        )}

        {/* End measurement (symbol end to wall end) */}
        {distanceFromWallEnd > 0.01 && endSegments.length > 0 && (
          <>
            <SimpleLineSegments
              segments={endSegments}
              color={lineColor}
              lineWidth={0.1}
              renderOrder={997}
              depthTest={false}
            />
            <group position={endTextPosition} rotation={[0, 0, textRotation]}>
              <mesh position={[0, 0, -0.01]} renderOrder={998}>
                <planeGeometry args={[rulerTextBoxWidth + rulerTextBoxPadding, rulerTextBoxHeight]} />
                <meshBasicMaterial
                  color={renderStore.labelBackgroundColor}
                  depthTest={false}
                  depthWrite={false}
                />
              </mesh>
              <SimpleText
                position={[0, 0, 0.01]}
                color={textColor}
                fontSize={rulerTextFontSize}
                renderOrder={999}
                depthTest={false}
              >
                {`${formatLength(distanceFromWallEnd, editorStore.displayLengthUnit)}`}
              </SimpleText>
            </group>
          </>
        )}
      </group>
    );
  }
);

// Export without React.memo to ensure real-time updates during drag
export const AttachmentMeasurementLines = AttachmentMeasurementLinesComponent;
